import React from 'react';

import {
    configLayout,
    setWidgetList,
    setApiResponse,
    setWidgetDetailsLoader,
    setDashboardWidgetIds,
    removeWidgetLoadingState,
} from '@fiji/common/src/features/dashboardManagement/dashboardSlice';
import { api } from '@fiji/common/src/app/api/baseApi';
import { selectedOrg } from '@fiji/common/src/features/orgManagement/orgSlice';
import { useGetWidgetDetails } from '@fiji/common/src/hooks';
import { customEndpointSelector, isWidgetApplicable, isWidgetConfigured } from '@fiji/common/src/utils/helpers';
import { useLocation, useParams } from 'react-router-dom';
import { useGetDashboardByIdQuery } from '@fiji/common/src/features/dashboardManagement/dashboardApi';
import {
    useGetDeviceByIdQuery,
    useGetDeviceByProfileQuery,
} from '@fiji/common/src/features/deviceManagement/deviceApi';
import { useGetAllFeatureFlagQuery } from '@fiji/common/src/features/featureFlag/featureFlagApi';
import { useAppDispatch, useTypedSelector } from '@fiji/common/src/app/store';
import { useLazyGetDashboardWidgetByIdQuery as useLazyGetWidgetByIdQuery } from '@fiji/common/src/features/widgetManagement/widgetApi';
import { FeatureFlagData } from '@fiji/common/src/types';

type ReturnProps = {
    isLoading: boolean;
    widgetConfigHandler: any;
};

export const useConfigureWidgets = (skip?: boolean): ReturnProps => {
    const dispatch = useAppDispatch();

    const { state } = useLocation();
    const { dashboardId, assignId } = useParams();

    const currentOrg = useTypedSelector(selectedOrg);
    const widgets = useTypedSelector((rootState) => rootState.dashboard.widgetList);
    const layout = useTypedSelector((rootState) => rootState.dashboard.layout);
    const widgetIds = useTypedSelector((rootstate) => rootstate.dashboard.dashboardWidgetIds);
    const removedWidgets = useTypedSelector((rootstate) => rootstate.dashboard.removedWidgets);
    const loadingState = useTypedSelector((rootState) => rootState.dashboard.loaders);
    const apiResponses = useTypedSelector((rootState) => rootState.dashboard.apiResponse);

    const [getWidgetConfig, { isFetching, isLoading }] = useLazyGetWidgetByIdQuery();

    const {
        currentData: dashboard,
        isLoading: dashboardLoading,
        isFetching: dashboardFetching,
    }: any = useGetDashboardByIdQuery(dashboardId, {
        skip: !dashboardId || !currentOrg.id,
        refetchOnMountOrArgChange: true,
    });

    const dashboardAssignType: string | undefined = dashboard?.data?.assignType;
    const stateAssignType: string | undefined = state?.assignType;
    const resolvedAssignType: string | undefined = dashboardAssignType ?? stateAssignType;
    const isSourceDashboard = resolvedAssignType === 'SOURCE';
    const isDeviceDashboard = resolvedAssignType === 'DEVICE';
    const sourceProfileId: string | undefined = dashboard?.data?.assignTypeIds?.[0];

    const { data: deviceDetails } = useGetDeviceByProfileQuery(sourceProfileId, {
        skip: !isSourceDashboard || !sourceProfileId || assignId !== sourceProfileId,
        refetchOnMountOrArgChange: true,
    });

    const resolvedDeviceId: string | undefined =
        !state?.isDefaultRoot && isSourceDashboard ? assignId : deviceDetails?.data?.id;

    const resolveDeviceContextId = (): string | undefined => {
        if (isSourceDashboard) {
            // assignId is the profile/source id here (matches dashboard.data.assignTypeIds[0]);
            // the real device id comes from the resolved profile lookup.
            return resolvedDeviceId;
        }
        if (isDeviceDashboard) {
            return assignId ?? state?.assignId;
        }
        return undefined;
    };
    const deviceContextId = resolveDeviceContextId();

    const { currentData: deviceData }: any = useGetDeviceByIdQuery(deviceContextId, {
        skip: state?.['assignType'] === 'GROUP' || !deviceContextId,
    });
    const { data: featureFlagData } = useGetAllFeatureFlagQuery({});
    const isOntologyActivationEnabled = React.useMemo(
        () =>
            featureFlagData?.data?.some(
                (featureFlag: FeatureFlagData) =>
                    typeof featureFlag?.featureFlagName === 'string' &&
                    featureFlag?.featureFlagName?.toLowerCase() === 'instance-ontology-support' &&
                    featureFlag?.enabled === true
            ) ?? false,
        [featureFlagData]
    );

    const { payload, response: widgetResponse } = useGetWidgetDetails({
        selectorType: 'dashboard',
        dataGetter: {
            ...(state?.assignType === 'GROUP' ? { groupId: assignId } : { deviceId: deviceContextId }),
        },
    });

    const widgetConfigHandler = (widgetId: any): void => {
        const widgetArgs =
            isOntologyActivationEnabled && deviceContextId ? { widgetId, deviceId: deviceContextId } : widgetId;
        // Only a re-fetch overwrites a config that getValues already merged data into
        // (e.g. schedule's config.secondary); a first load has no loader to clear anyway.
        const isRefetch = widgets?.some((widget: any) => widget?.id === widgetId);
        getWidgetConfig(widgetArgs)
            .then((res: any) => {
                dispatch(setWidgetList({ data: res?.data?.data, widgetId: widgetId }));
                if (isRefetch) {
                    dispatch(removeWidgetLoadingState(widgetId));
                }
            })
            .catch((err: any) => {
                console.error(err);
            });
    };

    const getWidgetApplicableType = (widget: any): any => {
        if (
            state?.['assignType'] === 'GROUP' ||
            (widget?.widgetType?.id === 'device_list' && deviceData?.data['wrapperDeviceType'] === 'Gateway')
        ) {
            return 'GROUP';
        }
        return 'DEVICE';
    };

    // SOURCE dashboards must wait for the profile→device lookup before firing
    // getWidgetConfig; otherwise the request would go out without a deviceId and
    // instance-ontology resolution on the backend would be wrong.
    const isSourceWidgetFetchPending = isSourceDashboard && !deviceContextId;

    React.useEffect(() => {
        if (skip || !widgetIds?.length) return;
        // When resolvedDeviceId arrives, this effect re-runs (see deps) and picks up
        // any widgetIds whose config hasn't been fetched yet.
        if (isSourceWidgetFetchPending) return;

        widgetIds?.forEach((widgetId: any) => {
            const existWidgetIndex = widgets.findIndex((widget: any) => widget?.id === widgetId);
            if (existWidgetIndex === -1 || isOntologyActivationEnabled) {
                widgetConfigHandler(widgetId);
            }
        });
    }, [widgetIds, isSourceWidgetFetchPending, isOntologyActivationEnabled]);

    React.useEffect(() => {
        if (dashboard?.data?.widgetIdList?.length && !skip) {
            dashboard?.data?.widgetIdList?.forEach((item: any) => {
                const existingWidgetIndex = widgetIds.findIndex((widget: any) => widget === item);
                const removedWidget = removedWidgets?.indexOf(item);

                if (existingWidgetIndex === -1 && removedWidget === -1) {
                    dispatch(setDashboardWidgetIds(item));
                }
            });
        }
    }, [dashboard?.data?.widgetIdList, dashboardFetching]);

    React.useEffect(() => {
        // Only reconfigure layout when widget list length changes (add/remove)
        // or on initial load, not during widget configuration updates
        if (!skip && dashboard?.data) {
            const currentWidgetCount = widgets?.filter((widget: any) => !widget?.config?.isPinned)?.length || 0;

            // Only trigger layout reconfiguration if widget count doesn't match
            // or if this is the initial load (no existing layout)
            if (currentWidgetCount !== layout?.length || !layout || layout.length === 0) {
                dispatch(configLayout(dashboard?.data));
            }
        }
    }, [widgets?.length, dashboard?.data?.widgetIdList?.length]);

    React.useEffect(() => {
        if (widgets?.length === widgetIds?.length && !skip) {
            getValues();
        }
    }, [widgets, widgetIds]);

    async function setWidgetValue(widget: any, endpoint: any): Promise<any> {
        const selectedWidget = widgets?.find((prevWidget: any) => prevWidget?.id === widget?.id);
        const selectedWidgetConfig = selectedWidget?.config;

        const key = `${widget?.widgetType?.id}${
            endpoint?.endpoint === 'getDeviceAlertCount' ||
            endpoint?.endpoint === 'getAvailableCommands' ||
            endpoint?.endpoint === 'getLoadGroupTrends' ||
            endpoint?.endpoint === 'getEnvironmentalStressMonitoringMetrics' ||
            endpoint?.endpoint === 'getEnvironmentalStressMonitoringFilter'
                ? `-${endpoint?.endpoint}`
                : ''
        }`;

        const payloadBuilder = payload?.[key] || payload?.['default'];
        const newPayload = payloadBuilder
            ? await payloadBuilder(selectedWidgetConfig, selectedWidget?.selectedEntities)
            : undefined;

        return dispatch((api as any)?.endpoints?.[endpoint?.endpoint].initiate(newPayload, { forceRefetch: true }));
    }

    React.useEffect(() => {
        if (apiResponses?.length && !skip) {
            apiResponses.forEach(({ responses, type, widgetId }: any) => {
                if (!loadingState[widgetId]) {
                    (widgetResponse[type] || widgetResponse['default'])?.(responses, widgetId);
                }
            });
        }
    }, [apiResponses, widgets]);

    function getValues(): void {
        widgets?.forEach((widget: any) => {
            if (
                widget?.widgetType?.id === 'virtual_loads' ||
                widget?.widgetType?.internalCdnJsLink ||
                !isWidgetConfigured(widget) ||
                !isWidgetApplicable(widget, getWidgetApplicableType(widget))
            ) {
                dispatch(setWidgetDetailsLoader({ key: widget?.id, value: 'loaded' }));
            } else if (!loadingState[widget?.id]) {
                const promises: any = [];

                for (let i = 0; i < customEndpointSelector(widget?.widgetType?.id)?.length; i++) {
                    promises.push(
                        setWidgetValue(widget, customEndpointSelector(widget?.widgetType?.id, widget?.config?.type)[i])
                    );
                }
                Promise.all(promises)
                    .then((responses) => {
                        dispatch(setApiResponse({ widget, responses }));
                    })
                    .catch((e) => {
                        console.error(e);
                        // Handle errors here
                    });
            }
        });
    }

    return {
        isLoading: dashboardLoading || dashboardFetching || isLoading || isFetching,
        widgetConfigHandler: widgetConfigHandler,
    };
};
